import {
  CellState,
  CellType,
  ClueType,
  FeedFormat,
  ICell,
  IClue,
  IFeedMetaData,
  IPuzzleData,
  PuzzleType,
} from 'js/crossword/types';

enum LarousseFeedItemType {
  ACROSS = "ACROSS",
  DOWN = "DOWN",
}

const directionMap = {
  [LarousseFeedItemType.ACROSS]: ClueType.Across,
  [LarousseFeedItemType.DOWN]: ClueType.Down,
};

const charIdentifiers = [
  "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M",
  "N", "O", "P", "Q", "R", "S", "T", "U", "W", "X", "Y", "Z",
];

export default function convertLarousse(rawData: ILarousseFeedRawData): IPuzzleData {
  const crossWordData = remapRawData(rawData);

  const { columns, rows } = getBoardSize(crossWordData.data.items);

  const cells: ICell[] = new Array(columns * rows).fill(null)
    .map((_, index) => {
      return createOrUpdateCell({
        x: (index % columns),
        y: Math.floor((index) / columns),
        id: index.toString(),
      });
    });

  const usedIdentifiers: Record<string, boolean> = {};
  const clues: IClue[] = crossWordData.data.items.map((item, index) => {
    const identifier = getClueIdentifier(
      item,
      (identKey) => {
        if (!usedIdentifiers[identKey]){
          usedIdentifiers[identKey] = true;
          return true;
        }

        return false;
      }
    );

    const clue = createClue(item, index, identifier);
    const answer = cleanUpText(item.answer.text).toUpperCase();
    clue.cells = answer.split("").concat([""])
      .map((solution, index) => {
        if (solution === "") return null;

        const cellIndex = getCellIndexByPosition(
          cells,
          getCellPosition(item.answer, index),
        );
        
        if (cellIndex === -1) return null;

        cells[cellIndex] = createOrUpdateCell({
          ...cells[cellIndex],
          solution,
          type: CellType.Regular,
        })

        return cells[cellIndex].id;
      })
      .filter(Boolean) as string[];

    return clue;
  });

  return {
    id: crossWordData.metaData.id,
    type: PuzzleType.Regular,
    metaData: {
      title: crossWordData.metaData.title || "Mots croisés Larousse",
      author: crossWordData.metaData.author,
      date: crossWordData.metaData.date,
      format: FeedFormat.LAROUSSE,
    },
    columns,
    rows,
    clues,
    cells: fillIdentifierCells(cells),
  };
}

function remapRawData(rawData: ILarousseFeedRawData): ILarousseFeedData {
  const { metaData, data } = rawData;
  return {
    metaData,
    data: {
      copyright: data.Copyright[0] || "",
      items: data.Item.map((item) => ({
        question: remapRawQA(item.Question),
        answer: remapRawQA(item.Answer),
      })),
    },
  };
}

function remapRawQA<T>(rawQA: any): T {
  const { _: text , $ } = rawQA[0] || { $: {}, _: "" };
  return { text, ...$ };
}

function getBoardSize(items: Array<ILarousseFeedItem>): {
  columns: number;
  rows: number;
} {
  return items.reduce(
    (prevValue, item) => {
      const x = Number(item.answer.x);
      const columns = Math.max(prevValue.columns, x + 1);
      
      const y = Number(item.answer.y);
      const rows = Math.max(prevValue.rows, y + 1);

      return { columns, rows };
    },
    { columns: 0, rows: 0 },
  );
}

function cleanUpText(text: string): string {
  return text.replace(/(\r\n|\n|\r)/gm, "");
}

function createClue(
  item: ILarousseFeedItem,
  index: number,
  identifier: string,
): IClue {
  const { direction } = item.answer;
  const type = directionMap[direction];

  const text = cleanUpText(item.question.text).split("");
  text[0] = text[0].toUpperCase();

  return {
    type,
    identifier,
    id: index.toString(),
    number: index + 1,
    text: text.join(""),
    cells: [],
  };
}

function createOrUpdateCell(initialData: Partial<ICell>): ICell {
  return {
    x: 0,
    y: 0,
    letter: "",
    id: "",
    number: null,
    circle: false,
    solution: "",
    type: CellType.Empty,
    state: CellState.Normal,
    ...initialData,
  };
}

function getClueIdentifier(
  item: ILarousseFeedItem,
  isValidIdentifier: (identKey: string) => boolean,
): string {
  const { x: answerX, y: answerY, direction } = item.answer;
  const type = directionMap[direction];

  let identifier = type === ClueType.Across ? answerY : charIdentifiers[Number(answerX) - 1];
  const identKey = `${direction}_${identifier}`;
  
  if (!isValidIdentifier(identKey)) {
    const spaces = identifier.split("").fill(" ").join("");
    identifier = new Array(identifier.length).fill(" ").concat([spaces]).join("")
  };

  return identifier;
}

function getCellPosition(answer: ILarousseFeedItem["answer"], index: number): IPosition {
  const { x: answerX, y: answerY, direction } = answer;
  
  const x = direction === LarousseFeedItemType.ACROSS ? Number(answerX) + index : Number(answerX);
  const y = direction === LarousseFeedItemType.DOWN ? Number(answerY) + index : Number(answerY);

  return { x, y };
}

function getCellIndexByPosition(cells: ICell[], pos: IPosition): number {
  return cells.findIndex((cell) => cell.x === pos.x && cell.y === pos.y);
}

function fillIdentifierCells(cells: ICell[]): ICell[] {
  return cells.map((cell) => {
    if (cell.x !== 0 && cell.y !== 0) return cell;

    const newCell = {
      ...cell,
      type: CellType.Identifier,
    };

    if (newCell.x === 0 && newCell.y === 0) return newCell;
    
    const identifier = String(newCell.x === 0 ? newCell.y : charIdentifiers[newCell.x - 1]);

    return { ...newCell, identifier };
  });
}

// types
interface ILarousseFeedMetaData extends IFeedMetaData {
  title: string;
}

interface ILarousseFeedRawData {
  metaData: ILarousseFeedMetaData;
  data: {
    $: { GameId: string; };
    Copyright: Array<string>;
    Item: Array<ILarousseFeedRawItem>;
  };
}

interface ILarousseFeedRawItem {
  Question: Array<{
    _: string; //text
    $: {
      position: "UPPER";
      size: "SIMPLE";
      theme: "yes" | "no";
      type: string;
      x: string;
      y: string;
    }
  }>;
  Answer: Array<{
    _: string; //text
    $: {
      x: string;
      y: string;
      direction: LarousseFeedItemType;
      hyphen: "yes" | "no";
    }
  }>;
}
interface ILarousseFeedData {
  metaData: ILarousseFeedMetaData;
  data: {
    copyright: string;
    items: Array<ILarousseFeedItem>;
  };
}

interface ILarousseFeedItem {
  question: {
    text: string;
    position: "UPPER";
    size: "SIMPLE";
    theme: "yes" | "no";
    type: string;
    x: string;
    y: string;
  };
  answer: {
    text: string;
    x: string;
    y: string;
    direction: LarousseFeedItemType;
    hyphen: "yes" | "no";
  };
}

interface IPosition {
  x: number;
  y: number;
}
